// JUST FIREBASE LOGIC, HANDLES HTTP/FIREBASE REQUESTS
// IMPORTANT	--	NO MERGE WITH REDUX LOGIC --

import { collection, doc, getDoc, getDocs, addDoc, updateDoc, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { convertTimestampsToUnix } from '../../utils/dateUtils';
import { readICalendarRule } from '../../utils/calendarUtils';
import { AVAILABILITY_STATUS } from '../../utils/constants/availability.constants';

// Generate iCalendar rule using cloud function
export const generateICalendarRule = async (eventData) => {
  try {
    const generateICalendarRuleCallable = httpsCallable(functions, 'generateICalendarRule');
    const result = await generateICalendarRuleCallable(eventData);
    return result.data.rule;
  } catch (error) {
    throw new Error('Error generating iCalendar rule');
  }
};

// Get all availability events for a user

export const getAllAvailabilityEvents = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required to get availability events');
  }

  try {
    const eventsCollectionRef = collection(db, 'availability');
    const q = query(eventsCollectionRef, where('eid', '==', userId));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.warn(`No availability events found for user ${userId}`);
      return [];
    }

    return querySnapshot.docs.map((doc) => {
      const rawData = convertTimestampsToUnix(doc.data());
      const parsedRule = readICalendarRule(rawData.rule, rawData.type);

      return {
        id: doc.id,
        ...rawData,
        ...parsedRule,
      };
    });
  } catch (error) {
    console.error('Error fetching availability events:', error);
    throw error;
  }
};

// Get a single availability event
export const getAvailabilityEvent = async (userId, eventId) => {
  if (!userId || !eventId) {
    throw new Error('User ID and Event ID are required');
  }

  try {
    const eventDocRef = doc(db, `users/${userId}/availability/${eventId}`);
    const eventDocSnap = await getDoc(eventDocRef);

    if (!eventDocSnap.exists()) {
      throw new Error(`Event with ID ${eventId} not found for user ${userId}`);
    }

    return {
      id: eventDocSnap.id,
      ...convertTimestampsToUnix(eventDocSnap.data()),
    };
  } catch (error) {
    console.error('Error fetching availability event:', error);
    throw error;
  }
};

// Create a new availability event
export const createAvailabilityEvent = async (eventData) => {
  try {
    const eventsCollectionRef = collection(db, 'availability');
    const docRef = await addDoc(eventsCollectionRef, eventData);
    return {
      id: docRef.id,
      ...convertTimestampsToUnix(eventData),
    };
  } catch (error) {
    console.error('Error creating availability event:', error);
    throw error;
  }
};

// Update an availability event
export const updateAvailabilityEvent = async (eventId, eventData) => {
  if (!eventId) {
    throw new Error('Event ID is required');
  }

  try {
    const eventDocRef = doc(db, 'availability', eventId);
    await updateDoc(eventDocRef, eventData);
    return {
      id: eventId,
      ...convertTimestampsToUnix(eventData),
    };
  } catch (error) {
    console.error('Error updating availability event:', error);
    throw error;
  }
};

// Delete an availability event
export const deleteAvailabilityEvent = async (userId, eventId) => {
  if (!userId || !eventId) {
    throw new Error('User ID and Event ID are required');
  }

  try {
    const eventDocRef = doc(db, 'availability', eventId);
    await updateDoc(eventDocRef, { status: AVAILABILITY_STATUS.DELETED });
    return eventId;
  } catch (error) {
    console.error('Error deleting availability event:', error);
    throw error;
  }
};
